/**
 * resolveTokens(ds, theme) → { tokens, cssVars, rsdTheme }
 *
 *   tokens   — generate(brand) with sparse overrides applied (derived; not stored)
 *   cssVars  — deterministic `--wb-*` map for web (preview + workerd publish)
 *   rsdTheme — the resolved Tokens, consumed directly by RSD css.create on native
 *
 * Pure. `cssVars` key order is stable (insertion order below) so the emitted CSS
 * is byte-identical across runtimes.
 */
import { fontStack } from "./fontStack.js";
import { generate } from "./generate.js";
import type { DesignSystem } from "./designSystem.js";
import type { SiteTheme } from "./theme.js";
import type { Tokens, TokenOverrides, ColorScaleStep } from "./tokens.js";

const SCALE_STEPS: readonly ColorScaleStep[] = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
];

/** Deep-merge sparse overrides onto the generated tokens (overrides win). */
export function applyOverrides<T>(base: T, over: unknown): T {
  if (over == null || typeof over !== "object") return base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, ov] of Object.entries(over as Record<string, unknown>)) {
    const bv = (base as Record<string, unknown>)[k];
    out[k] =
      ov &&
      typeof ov === "object" &&
      !Array.isArray(ov) &&
      bv &&
      typeof bv === "object" &&
      !Array.isArray(bv)
        ? applyOverrides(bv, ov)
        : ov;
  }
  return out as T;
}

const px = (n: number): string => `${n}px`;

/** Flatten Tokens → a stable, ordered `--wb-*` CSS custom-property map. */
export function tokensToCssVars(t: Tokens): Record<string, string> {
  const v: Record<string, string> = {};
  // colors — scales then semantic roles
  for (const step of SCALE_STEPS)
    v[`--wb-color-primary-${step}`] = t.color.primary[step];
  for (const step of SCALE_STEPS)
    v[`--wb-color-neutral-${step}`] = t.color.neutral[step];
  v["--wb-color-background"] = t.color.background;
  v["--wb-color-foreground"] = t.color.foreground;
  v["--wb-color-primary"] = t.color.primarySolid;
  v["--wb-color-primary-foreground"] = t.color.primaryForeground;
  v["--wb-color-accent"] = t.color.accent;
  v["--wb-color-accent-foreground"] = t.color.accentForeground;
  v["--wb-color-muted"] = t.color.muted;
  v["--wb-color-muted-foreground"] = t.color.mutedForeground;
  v["--wb-color-border"] = t.color.border;
  v["--wb-color-ring"] = t.color.ring;
  // space
  for (const k of ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"] as const) {
    v[`--wb-space-${k}`] = px(t.space[k]);
  }
  // radius
  for (const k of ["none", "sm", "md", "lg", "full"] as const) {
    v[`--wb-radius-${k}`] = px(t.radius[k]);
  }
  // typography
  v["--wb-font-heading"] = fontStack(t.typography.fontFamily.heading);
  v["--wb-font-body"] = fontStack(t.typography.fontFamily.body);
  v["--wb-font-mono"] = fontStack(t.typography.fontFamily.mono, "mono");
  for (const k of [
    "xs",
    "sm",
    "base",
    "lg",
    "xl",
    "2xl",
    "3xl",
    "4xl",
  ] as const) {
    v[`--wb-font-size-${k}`] = px(t.typography.fontSize[k]);
  }
  for (const k of ["normal", "medium", "semibold", "bold"] as const) {
    v[`--wb-font-weight-${k}`] = String(t.typography.fontWeight[k]);
  }
  for (const k of ["tight", "normal", "relaxed"] as const) {
    v[`--wb-line-height-${k}`] = String(t.typography.lineHeight[k]);
  }
  // shadow
  for (const k of ["sm", "md", "lg"] as const)
    v[`--wb-shadow-${k}`] = t.shadow[k];
  // border
  v["--wb-border-thin"] = px(t.border.thin);
  v["--wb-border-thick"] = px(t.border.thick);
  return v;
}

/** Serialize the CSS-var map to a deterministic `:root { … }` block. */
export function cssVarsToBlock(
  vars: Record<string, string>,
  selector = ":root",
): string {
  const body = Object.entries(vars)
    .map(([k, val]) => `  ${k}: ${val};`)
    .join("\n");
  return `${selector} {\n${body}\n}`;
}

export interface ResolvedTheme {
  readonly tokens: Tokens;
  readonly cssVars: Record<string, string>;
  /** Same resolved tokens — consumed by RSD css.create() on native. */
  readonly rsdTheme: Tokens;
}

export function resolveTokens(
  ds: DesignSystem,
  theme: SiteTheme,
): ResolvedTheme {
  const base = generate(ds, theme.brand, theme.generationVersion);
  const tokens = applyOverrides<Tokens>(
    base,
    theme.tokenOverrides as TokenOverrides | undefined,
  );
  return { tokens, cssVars: tokensToCssVars(tokens), rsdTheme: tokens };
}
